import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MatDialog } from '@angular/material/dialog';
import { IEmpresa, IEmpresaDetalle } from './interfaces/empresa.interface';
import { EmpresaService } from './services/empresa.service';
import { OpcionesService } from './services/opciones.service';
import { IOpcion } from './interfaces/opciones.interface';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';

@Component({
  selector: 'app-empresa-form',
  templateUrl: './empresa-form.component.html',
  providers: [MessageService]
})
export class EmpresaFormComponent implements OnInit, OnChanges {
  @Input() model: IEmpresa | null = null;
  @Output() onSave = new EventEmitter<{ success: boolean; message: string }>();
  form!: FormGroup;
  loading: boolean = false;
  estatusOpciones: IOpcion[] = [];
  estadosOpciones: IOpcion[] = [];

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private empresaService: EmpresaService,
    private opcionesService: OpcionesService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadOpciones();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['model']) {
      if (changes['model'].currentValue) {
        this.loadEmpresa();
      } else {
        this.initForm();
      }
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      identificador: [{ value: '', disabled: !!this.model }],
      nombre: [''],
      calle: [''],
      numext: [''],
      numint: [''],
      ciudad: [''],
      del_muni: [''],
      cve_estado: [''],
      estatus: [''],
      telefono: [''],
      telefono2: ['']
    });
  }

  loadOpciones(): void {
    // Cargar opciones de estatus
    this.opcionesService.getEstatus().subscribe({
      next: (response) => {
        if (response.success) {
          this.estatusOpciones = response.data;
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones de estatus:', error);
      }
    });

    // Cargar opciones de estados
    this.opcionesService.getEstados().subscribe({
      next: (response) => {
        if (response.success) {
          this.estadosOpciones = response.data;
        }
      },
      error: (error) => {
        console.error('Error al cargar opciones de estados:', error);
      }
    });
  }

  loadEmpresa(): void {
    if (!this.model?.identificador) return;
    
    this.loading = true;
    this.empresaService.getEmpresaByIdentificador(this.model.identificador).subscribe({
      next: (response) => {
        if (response.success && response.data.length > 0) {
          const empresa: IEmpresaDetalle = response.data[0];
          this.form.patchValue({
            identificador: empresa.identificador,
            nombre: empresa.nombre,
            calle: empresa.calle,
            numext: empresa.numext,
            numint: empresa.numint,
            ciudad: empresa.ciudad,
            del_muni: empresa.del_muni,
            cve_estado: empresa.cve_estado,
            estatus: empresa.estatus,
            telefono: empresa.telefono,
            telefono2: empresa.telefono2
          });
          this.form.get('identificador')?.disable();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudo cargar la empresa'
          });
        }
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar la empresa'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  create(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor verifique los datos ingresados'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 1,
      tope: '',
      identificador: formData.identificador,
      nombre: formData.nombre,
      calle: formData.calle,
      numext: formData.numext,
      numint: formData.numint,
      ciudad: formData.ciudad,
      del_muni: formData.del_muni,
      cve_estado: formData.cve_estado,
      estatus: formData.estatus,
      telefono: formData.telefono,
      telefono2: formData.telefono2,
      scoring: 0,
      politica: '',
      comentario: ''
    };

    this.empresaService.saveEmpresa(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Empresa creada correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al crear la empresa'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al crear la empresa'
        });
        this.loading = false;
      }
    });
  }

  update(): void {
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor verifique los datos ingresados'
      });
      return;
    }

    this.loading = true;
    const formData = this.form.getRawValue();
    const payload = {
      que: 2,
      tope: '',
      identificador: formData.identificador,
      nombre: formData.nombre,
      calle: formData.calle,
      numext: formData.numext,
      numint: formData.numint,
      ciudad: formData.ciudad,
      del_muni: formData.del_muni,
      cve_estado: formData.cve_estado,
      estatus: formData.estatus,
      telefono: formData.telefono,
      telefono2: formData.telefono2,
      scoring: 0,
      politica: '',
      comentario: ''
    };

    this.empresaService.updateEmpresa(payload).subscribe({
      next: (response) => {
        if (response.success) {
          this.onSave.emit({
            success: true,
            message: 'Empresa actualizada correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al actualizar la empresa'
          });
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al actualizar la empresa'
        });
        this.loading = false;
      }
    });
  }

  openCustomizables(): void {
    this.dialog.open(AbcEmpresasCustomizablesComponent, {
      width: '1000px',
      height: '1000px',
      disableClose: true,
      panelClass: ['custom-dialog-zindex'],
      hasBackdrop: true,
      autoFocus: false,
      data: {
        sTabla: 'cianeg',
        sClave: `${this.form.get('identificador')?.value}`,
        sNombre: this.form.get('nombre')?.value,
        sTipo: 'Opcion'
      }
    });
  }
} 